import React, { FC, useContext, useEffect, useState } from "react";

import { useIntl } from "react-intl";

import { Stack } from "@mui/material";

import { Flow, OfflineDays, ServiceAvailabilityException, Service } from "../../types/entities";
import {
	BusinessDataContext,
	LoginContext,
	LoginContextValues,
	MultistepContext,
	MultistepContextValues,
} from "../../types/context";
import { LangContext } from "../../types/internationalization";
import { flows } from "../../types/flows";

import { useWindowResize } from "../../hooks/useWindowResize";

import { LangSelect } from "./LangSelect";
import { RenderStepContent } from "../stepper/RenderStepContent";
import { HeaderTitle } from "../styled/HeaderTitle";

import { findComponentStep } from "../../utils/filter";
import { generateHeaderTitle } from "../../utils/generateTitle";
import { ProfileButton } from "../buttons/ProfileButton";
import { BackButton } from "../buttons/BackButton";
import { LogInOutButton } from "../buttons/LogInOutButton";

interface IMainValues {
	services: Service[];
	setServices: (newValue: Service[]) => void;
	offlineDays: OfflineDays[];
	serviceAvailabilityException: ServiceAvailabilityException[];
}

// Getting the business name from the .env file
const businessName = process.env.REACT_APP_BUSINESS_NAME;

// This is the main screen of the application, it receives the services, offline days and flows,
// so we can set that information in the BusinessDataContext, so all the child components can use them.
export const Main: FC<IMainValues> = ({
	services,
	offlineDays,
	serviceAvailabilityException,
}) => {
	const { formatMessage } = useIntl();
	const [selectedFlow, setSelectedFlow] = useState<Flow>(flows[0]);
	const { reservation, activeStep } = useContext(
		MultistepContext
	) as MultistepContextValues;
	const { isLogged, isCalledFromProfile } = useContext(
		LoginContext
	) as LoginContextValues;

	const { isMobile } = useWindowResize();
	const langContext = useContext(LangContext);
	const [headerTitle, setHeaderTitle] = useState("home.systemTitle");
	const [showBusinessName, setShowBusinessName] = useState(true);

	const profileStep = findComponentStep(selectedFlow, "Profile");

	//Setting the header title with the home title
	useEffect(() => {
		const { title, showBusinessName } = generateHeaderTitle(
			activeStep,
			selectedFlow,
			reservation,
			formatMessage,
			langContext!.locale,
			isCalledFromProfile
		);

		setHeaderTitle(title);
		setShowBusinessName(showBusinessName);

		// eslint-disable-next-line
	}, [langContext, activeStep]);

	//The visual component, wrapped by the business data (services, offline days, flows)
	//If we are in the first step, we render the Home component, otherwise we render the Stepper component
	return (
		<BusinessDataContext.Provider
			value={{
				services,
				offlineDays,
				serviceAvailabilityException,
				flows,
				selectedFlow,
				setSelectedFlow,
			}}
		>
			{
				/* If we are in the home step or the profile step, we won't show the back button */
				activeStep !== 0 && activeStep !== profileStep && <BackButton />
			}
			<Stack sx={styles.contentContainer} spacing={1}>
				<Stack
					direction="row"
					spacing={{ mobile: 1, xs: 2 }}
					justifyContent="center"
					sx={[
						styles.headerContainer,
						isMobile && styles.mobileHeaderContainer,
					]}
				>
					<LangSelect />
					{isLogged && <ProfileButton />}
					<LogInOutButton />
				</Stack>
				<HeaderTitle
					title={headerTitle}
					businessName={showBusinessName ? businessName : undefined}
				/>
				<Stack>
					<RenderStepContent setHeaderTitle={setHeaderTitle} />
				</Stack>
			</Stack>
		</BusinessDataContext.Provider>
	);
};

const styles = {
	contentContainer: {
		pt: "16px",
		px: { mobile: "24px", xs: "32px" },
	},
	headerContainer: {
		display: "flex",
		justifyContent: "flex-end",
	},
	mobileHeaderContainer: {
		pb: 2,
	},
	stepperContainer: {
		pt: "16px",
	},
};
